/**
 * EN / ES translations for the /internal-tools service page.
 * Imported and merged into the main translations object.
 */
export const internalToolsTranslations = {
  en: {
    internalTools: {
      eyebrow: "Kranbos Internal Tools",
      title: "Build the Tool Your Team Actually Needs",
      subtitle:
        "Kranbos designs practical internal software that gives your team one clear place to manage requests, information, documents, assignments, approvals, and operational work.",
      primaryCta: "Discuss an Internal Tool",
      secondaryCta: "See What We Build",

      mockTitle: "Request queue",
      mockSubtitle: "Example interface. Placeholder data.",
      mockColRequest: "Request",
      mockColOwner: "Owner",
      mockColStatus: "Status",
      mockColPriority: "Priority",
      mockRows: [
        {
          request: "New customer onboarding",
          owner: "A. Rivera",
          status: "Under review",
          priority: "High",
          flag: "Approval required",
        },
        {
          request: "Contractor access request",
          owner: "M. Chen",
          status: "Waiting for information",
          priority: "Medium",
          flag: "Missing information",
        },
        {
          request: "Service estimate approval",
          owner: "J. Whitfield",
          status: "Assigned",
          priority: "High",
          flag: "",
        },
        {
          request: "Missing customer documents",
          owner: "Unassigned",
          status: "New",
          priority: "Low",
          flag: "Missing information",
        },
        {
          request: "Location inspection follow-up",
          owner: "D. Okafor",
          status: "In progress",
          priority: "Medium",
          flag: "",
        },
      ],
      mockActivityTitle: "Recent activity",
      mockActivity: [
        "Estimate uploaded to “Service estimate approval”",
        "“Contractor access request” assigned to M. Chen",
        "Manager approval requested on “New customer onboarding”",
      ],

      problemEyebrow: "When existing software falls short",
      problemTitle: "Your team should not have to work around its tools",
      problemBody1:
        "Many businesses run important processes through a combination of email, spreadsheets, shared folders, messaging apps, and industry-specific software.",
      problemBody2:
        "Each tool may serve a purpose, but employees are often left to connect everything manually.",
      problemBody3:
        "They search across systems, copy information between records, maintain separate tracking sheets, ask for status updates, and depend on individual employees to remember what should happen next.",
      problemBody4:
        "Kranbos designs internal tools around the workflow your team actually follows, creating a clearer and more consistent place to complete the work.",
      problemCurrentTitle: "How it usually works today",
      problemCurrentItems: [
        "Requests arrive through multiple channels",
        "Information is stored in different systems",
        "Status is tracked manually",
        "Employees ask each other for updates",
        "Permissions are broader than necessary",
        "Managers cannot easily see bottlenecks",
      ],
      problemImprovedTitle: "How it can work instead",
      problemImprovedItems: [
        "One organized work queue",
        "Relevant information in one view",
        "Clear ownership and next steps",
        "Built-in approvals and status changes",
        "Controlled access by role",
        "Better visibility into incomplete work",
      ],

      capabilitiesEyebrow: "What we build",
      capabilitiesTitle: "Focused software for the work your business does every day",
      capabilitiesIntro:
        "An internal tool should make an important process easier to understand, manage, and complete. Kranbos focuses on practical interfaces that reduce confusion without introducing unnecessary complexity.",
      capability1Title: "Operations portals",
      capability1Text:
        "Give employees one place to view requests, update records, complete tasks, and manage day-to-day operational work.",
      capability2Title: "Intake and review systems",
      capability2Text:
        "Collect information through structured forms and route submissions to the right person for review, follow-up, or approval.",
      capability3Title: "Work queues and assignment tools",
      capability3Text:
        "Organize incoming work by status, priority, owner, location, customer, or deadline so the team knows what requires attention.",
      capability4Title: "Approval workflows",
      capability4Text:
        "Create clear review steps for estimates, documents, expenses, access requests, changes, or other important decisions.",
      capability5Title: "Contractor and partner portals",
      capability5Text:
        "Give outside personnel a controlled interface for viewing assignments, uploading documents, and completing approved actions.",
      capability6Title: "Administrative tools",
      capability6Text:
        "Manage users, permissions, service records, content, configurations, and operational data without relying on developers for routine changes.",

      useCasesEyebrow: "Common use cases",
      useCasesTitle: "One clear workspace can replace many manual workarounds",
      useCases: [
        {
          title: "Customer onboarding workspace",
          text: "Track forms, documents, approvals, assignments, communications, and completion status in one place.",
        },
        {
          title: "Service request manager",
          text: "Receive requests, categorize them, assign the right employee or contractor, and monitor progress through completion.",
        },
        {
          title: "Document review portal",
          text: "Organize files, flag missing information, collect feedback, and move documents through structured review stages.",
        },
        {
          title: "Employee and contractor access portal",
          text: "Control what different users can view, upload, approve, or modify based on their responsibilities.",
        },
        {
          title: "Exception management queue",
          text: "Surface incomplete records, failed automations, overdue tasks, and items that need human attention.",
        },
        {
          title: "Multi-location operations hub",
          text: "Manage activity, responsibilities, requests, and status across multiple offices, properties, stores, or service areas.",
        },
        {
          title: "Configuration and content manager",
          text: "Allow authorized staff to update operational rules, templates, service options, or reusable content without changing code.",
        },
        {
          title: "Record matching and reconciliation tool",
          text: "Help employees compare information from different systems, identify duplicates, and resolve mismatched records.",
        },
      ],
      useCasesNote:
        "These are examples rather than fixed packages. The right tool depends on the process, the people using it, and the systems that already contain the business’s information.",

      exampleEyebrow: "Example internal tool",
      exampleTitle: "A single operations hub for a multi-location service company",
      exampleIntro1:
        "A service company with multiple locations may receive customer requests through phone calls, website forms, emails, employees, and outside partners.",
      exampleIntro2:
        "Employees may then use separate systems for customer records, scheduling, documents, invoicing, communication, and contractor coordination.",
      exampleIntro3:
        "Instead of forcing the team to manage each request manually across every platform, Kranbos could build an internal operations hub that brings the relevant work into one organized interface.",
      exampleFeatures: [
        {
          title: "Request inbox",
          text: "View incoming customer, employee, and location requests in one queue.",
        },
        {
          title: "Request details",
          text: "See the customer, location, category, supporting documents, recent activity, and relevant information from connected systems.",
        },
        {
          title: "Assignment",
          text: "Assign the request to an employee, manager, technician, or approved contractor.",
        },
        {
          title: "Status tracking",
          text: "Move work through stages such as New, Waiting for Information, Under Review, Approved, Assigned, In Progress, and Complete.",
        },
        {
          title: "Approval controls",
          text: "Require manager approval before estimates, expenses, access changes, or other important actions move forward.",
        },
        {
          title: "Activity history",
          text: "See comments, uploads, assignments, status changes, and completed actions in chronological order.",
        },
        {
          title: "Exception alerts",
          text: "Highlight overdue work, missing information, failed integrations, and requests that have remained inactive.",
        },
        {
          title: "Management overview",
          text: "Allow managers to see workload, bottlenecks, and unresolved issues across locations.",
        },
      ],
      exampleWorkflowTitle: "A typical path through the tool",
      exampleWorkflowSteps: [
        "Request received",
        "Information reviewed",
        "Missing details requested",
        "Manager approval",
        "Work assigned",
        "Completion confirmed",
        "Record updated",
      ],
      exampleClosing:
        "The internal tool does not have to replace every existing platform. It can become the operational layer that helps employees interact with those systems more consistently.",
      exampleDisclaimer:
        "This is an illustrative example, not a real client project. Any names, records, and figures shown are placeholders.",

      systemsEyebrow: "Work with what you already have",
      systemsTitle: "A better interface without replacing your entire technology stack",
      systemsBody1:
        "Most internal tools need to work alongside software the business already depends on.",
      systemsBody2:
        "Kranbos evaluates how the new tool can securely exchange information with existing platforms, reduce duplicate entry, and give users access to the information required for their work.",
      systemsGroups: [
        {
          title: "Customer and communication",
          items: ["CRM and customer management platforms", "Email and calendars", "Scheduling systems"],
        },
        {
          title: "Documents and signatures",
          items: ["Document storage", "Adobe Acrobat", "Electronic signature platforms"],
        },
        {
          title: "Workplace suites",
          items: ["Microsoft 365", "Google Workspace", "Project and task management tools"],
        },
        {
          title: "Operations and data",
          items: [
            "Accounting and invoicing software",
            "Spreadsheets",
            "Industry-specific platforms",
            "Internal databases",
            "Approved third-party APIs",
          ],
        },
      ],
      systemsSupporting:
        "Depending on the platform, information may be synchronized automatically, retrieved when needed, linked to the original source, or reviewed through a controlled manual step.",
      systemsAdditional:
        "When a system does not support the required integration, Kranbos identifies the safest practical alternative rather than promising functionality the platform cannot reliably provide.",

      securityEyebrow: "Designed around responsibility",
      securityTitle: "Show each person only what they need to do their job",
      securityBody1:
        "An internal tool can provide a more controlled experience than giving every employee or contractor full access to multiple business platforms.",
      securityBody2:
        "Kranbos can design the interface around separate accounts, user roles, limited actions, approval requirements, and clearer activity history.",
      securityUsefulLabel: "This is especially useful when the business works with:",
      securityUsefulItems: [
        "Outside contractors",
        "Virtual assistants",
        "Overseas personnel",
        "Temporary employees",
        "Multiple departments",
        "Multiple locations",
        "External partners",
      ],
      security1Title: "Role-based access",
      security1Text:
        "Control which sections, records, fields, and actions are available to each user.",
      security2Title: "Approval checkpoints",
      security2Text:
        "Require authorized review before sensitive or irreversible actions are completed.",
      security3Title: "Controlled data exposure",
      security3Text:
        "Display only the information required for the assigned task instead of providing broad system access.",
      security4Title: "Activity history",
      security4Text: "Record important assignments, updates, approvals, uploads, and status changes.",
      securityDisclaimer:
        "Kranbos implements practical operational and application controls as part of the solution. Formal cybersecurity audits, legal compliance reviews, and security certifications may require a specialized provider.",

      comboEyebrow: "Interface + automation",
      comboTitle: "The tool gives your team control. Automation keeps the work moving.",
      comboBody1: "Internal tools and workflow automation often work together.",
      comboBody2:
        "The internal tool gives employees a clear place to review information, make decisions, and complete work. Automation handles repetitive actions in the background, such as creating records, sending reminders, requesting documents, updating systems, and notifying the right people.",
      comboToolTitle: "Internal Tool",
      comboToolItems: [
        "View and manage work",
        "Review information",
        "Make decisions",
        "Assign responsibility",
        "Approve or reject requests",
        "Resolve exceptions",
        "Update status",
      ],
      comboAutomationTitle: "Workflow Automation",
      comboAutomationItems: [
        "Move data between systems",
        "Create records and tasks",
        "Send notifications",
        "Request missing information",
        "Generate documents",
        "Escalate overdue work",
        "Synchronize approved updates",
      ],
      comboClosing:
        "Kranbos can build either capability independently or combine them into one operational solution.",
      comboCta: "Explore Workflow Automation",

      whenEyebrow: "The right solution",
      whenTitle: "Not every problem needs custom software",
      whenBody1:
        "Kranbos begins by understanding the business problem before recommending a technical solution.",
      whenBody2:
        "In some cases, an existing platform, a configuration change, or a workflow automation may be enough. A custom internal tool makes sense when the team needs a specific operational experience that existing products cannot provide efficiently.",
      whenYesTitle: "An internal tool may be appropriate when:",
      whenYesItems: [
        "Employees repeatedly work around existing software",
        "The same information must be reviewed across multiple systems",
        "A process requires custom roles, steps, or approvals",
        "Managers need a shared operational queue",
        "Contractors need restricted access",
        "Spreadsheet-based processes are becoming difficult to control",
        "Existing software includes too much complexity or not enough flexibility",
        "The workflow is important enough to justify a dedicated interface",
      ],
      whenNoTitle: "A custom tool may not be necessary when:",
      whenNoItems: [
        "An existing platform already solves the problem well",
        "The process is temporary or rarely used",
        "A simple automation can remove the bottleneck",
        "The workflow has not yet been clearly defined",
        "The business is not prepared to maintain or adopt a new process",
      ],
      whenClosing:
        "Our goal is to recommend the smallest practical solution that meaningfully improves the way the business operates.",

      processEyebrow: "How it works",
      processTitle: "Build around the people doing the work",
      processSteps: [
        {
          title: "Operational discovery",
          text: "We document the current process, users, responsibilities, systems, workarounds, and recurring problems.",
        },
        {
          title: "Tool definition",
          text: "We identify the essential screens, actions, roles, integrations, and business rules required for the first useful version.",
        },
        {
          title: "Prototype and validation",
          text: "We create a practical interface direction and confirm that it reflects how the team actually works before building the full solution.",
        },
        {
          title: "Development and testing",
          text: "We build the tool, connect approved systems, configure permissions, and test important workflows and edge cases.",
        },
        {
          title: "Launch and improvement",
          text: "We deploy the tool, document its use, support adoption, and improve it based on real operational feedback.",
        },
      ],

      outcomesEyebrow: "The result",
      outcomesTitle: "A clearer way to run the work behind the business",
      outcome1Title: "One organized workspace",
      outcome1Text:
        "Give employees a consistent place to find, review, and complete operational work.",
      outcome2Title: "Less system switching",
      outcome2Text:
        "Bring the information and actions required for a process into a more focused experience.",
      outcome3Title: "Clearer responsibility",
      outcome3Text: "Make ownership, status, approvals, and next steps easier to understand.",
      outcome4Title: "Better operational control",
      outcome4Text:
        "Reduce dependence on scattered spreadsheets, inboxes, informal messages, and employee memory.",

      relatedTitle: "The best solution may combine more than one service",
      relatedIntro:
        "Kranbos can combine internal tools, automation, reporting, and spreadsheet improvements into one practical operational system.",
      relatedCurrentLabel: "You are here",
      related1Text: "Give your team a focused interface for managing operational work.",
      related2Text:
        "Connect existing systems and automatically move information and actions forward.",
      related3Text: "Track activity, workload, bottlenecks, exceptions, and business performance.",
      related4Text:
        "Improve or replace spreadsheet-based processes that have become difficult to maintain.",

      finalTitle: "Give your team a better way to get the work done",
      finalBody:
        "Tell us where your current systems, spreadsheets, and manual processes are slowing the team down. We will help determine whether an internal tool, automation, or simpler solution is the right next step.",
      finalSecondaryCta: "Contact Us",
    },
  },
  es: {
    internalTools: {
      eyebrow: "Herramientas Internas Kranbos",
      title: "Construye la herramienta que tu equipo realmente necesita",
      subtitle:
        "Kranbos diseña software interno práctico que le da a tu equipo un solo lugar claro para gestionar solicitudes, información, documentos, asignaciones, aprobaciones y trabajo operativo.",
      primaryCta: "Hablemos de una Herramienta Interna",
      secondaryCta: "Ver qué construimos",

      mockTitle: "Cola de solicitudes",
      mockSubtitle: "Interfaz de ejemplo. Datos ficticios.",
      mockColRequest: "Solicitud",
      mockColOwner: "Responsable",
      mockColStatus: "Estado",
      mockColPriority: "Prioridad",
      mockRows: [
        {
          request: "Alta de nuevo cliente",
          owner: "A. Rivera",
          status: "En revisión",
          priority: "Alta",
          flag: "Requiere aprobación",
        },
        {
          request: "Solicitud de acceso de contratista",
          owner: "M. Chen",
          status: "Esperando información",
          priority: "Media",
          flag: "Falta información",
        },
        {
          request: "Aprobación de presupuesto de servicio",
          owner: "J. Whitfield",
          status: "Asignada",
          priority: "Alta",
          flag: "",
        },
        {
          request: "Documentos de cliente faltantes",
          owner: "Sin asignar",
          status: "Nueva",
          priority: "Baja",
          flag: "Falta información",
        },
        {
          request: "Seguimiento de inspección de sucursal",
          owner: "D. Okafor",
          status: "En curso",
          priority: "Media",
          flag: "",
        },
      ],
      mockActivityTitle: "Actividad reciente",
      mockActivity: [
        "Presupuesto cargado en “Aprobación de presupuesto de servicio”",
        "“Solicitud de acceso de contratista” asignada a M. Chen",
        "Aprobación de gerencia solicitada en “Alta de nuevo cliente”",
      ],

      problemEyebrow: "Cuando el software existente no alcanza",
      problemTitle: "Tu equipo no debería tener que trabajar esquivando sus herramientas",
      problemBody1:
        "Muchos negocios manejan procesos importantes con una combinación de correo, hojas de cálculo, carpetas compartidas, apps de mensajería y software específico de su industria.",
      problemBody2:
        "Cada herramienta puede cumplir una función, pero los empleados suelen quedar a cargo de conectar todo manualmente.",
      problemBody3:
        "Buscan en varios sistemas, copian información entre registros, mantienen planillas de seguimiento aparte, piden actualizaciones de estado y dependen de que alguien recuerde qué debe pasar después.",
      problemBody4:
        "Kranbos diseña herramientas internas alrededor del flujo de trabajo que tu equipo realmente sigue, creando un lugar más claro y consistente para completar el trabajo.",
      problemCurrentTitle: "Cómo suele funcionar hoy",
      problemCurrentItems: [
        "Las solicitudes llegan por múltiples canales",
        "La información vive en distintos sistemas",
        "El estado se registra manualmente",
        "Los empleados se piden actualizaciones entre sí",
        "Los permisos son más amplios de lo necesario",
        "Los gerentes no ven fácilmente los cuellos de botella",
      ],
      problemImprovedTitle: "Cómo puede funcionar en su lugar",
      problemImprovedItems: [
        "Una cola de trabajo organizada",
        "La información relevante en una sola vista",
        "Responsabilidad y próximos pasos claros",
        "Aprobaciones y cambios de estado integrados",
        "Acceso controlado según el rol",
        "Mejor visibilidad del trabajo incompleto",
      ],

      capabilitiesEyebrow: "Qué construimos",
      capabilitiesTitle: "Software enfocado en el trabajo que tu negocio hace todos los días",
      capabilitiesIntro:
        "Una herramienta interna debe hacer que un proceso importante sea más fácil de entender, gestionar y completar. Kranbos se enfoca en interfaces prácticas que reducen la confusión sin agregar complejidad innecesaria.",
      capability1Title: "Portales de operaciones",
      capability1Text:
        "Dale a los empleados un solo lugar para ver solicitudes, actualizar registros, completar tareas y gestionar el trabajo operativo del día a día.",
      capability2Title: "Sistemas de ingreso y revisión",
      capability2Text:
        "Recolecta información con formularios estructurados y dirige cada envío a la persona correcta para revisión, seguimiento o aprobación.",
      capability3Title: "Colas de trabajo y asignación",
      capability3Text:
        "Organiza el trabajo entrante por estado, prioridad, responsable, sucursal, cliente o fecha límite para que el equipo sepa qué requiere atención.",
      capability4Title: "Flujos de aprobación",
      capability4Text:
        "Crea pasos de revisión claros para presupuestos, documentos, gastos, solicitudes de acceso, cambios u otras decisiones importantes.",
      capability5Title: "Portales para contratistas y socios",
      capability5Text:
        "Dale al personal externo una interfaz controlada para ver asignaciones, subir documentos y completar acciones autorizadas.",
      capability6Title: "Herramientas administrativas",
      capability6Text:
        "Administra usuarios, permisos, registros de servicio, contenido, configuraciones y datos operativos sin depender de desarrolladores para cambios de rutina.",

      useCasesEyebrow: "Casos de uso comunes",
      useCasesTitle: "Un espacio de trabajo claro puede reemplazar muchos parches manuales",
      useCases: [
        {
          title: "Espacio de alta de clientes",
          text: "Sigue formularios, documentos, aprobaciones, asignaciones, comunicaciones y estado de avance en un solo lugar.",
        },
        {
          title: "Gestor de solicitudes de servicio",
          text: "Recibe solicitudes, clasifícalas, asigna al empleado o contratista correcto y monitorea el avance hasta completarlas.",
        },
        {
          title: "Portal de revisión de documentos",
          text: "Organiza archivos, marca información faltante, recoge comentarios y mueve documentos por etapas de revisión.",
        },
        {
          title: "Portal de acceso para empleados y contratistas",
          text: "Controla qué puede ver, subir, aprobar o modificar cada usuario según sus responsabilidades.",
        },
        {
          title: "Cola de excepciones",
          text: "Muestra registros incompletos, automatizaciones fallidas, tareas vencidas y casos que necesitan atención humana.",
        },
        {
          title: "Centro de operaciones multi-sucursal",
          text: "Gestiona actividad, responsabilidades, solicitudes y estado en varias oficinas, propiedades, tiendas o zonas de servicio.",
        },
        {
          title: "Gestor de configuración y contenido",
          text: "Permite que el personal autorizado actualice reglas operativas, plantillas, opciones de servicio o contenido reutilizable sin tocar código.",
        },
        {
          title: "Herramienta de conciliación de registros",
          text: "Ayuda a comparar información de distintos sistemas, identificar duplicados y resolver registros que no coinciden.",
        },
      ],
      useCasesNote:
        "Son ejemplos, no paquetes fijos. La herramienta correcta depende del proceso, de las personas que la usan y de los sistemas donde ya vive la información del negocio.",

      exampleEyebrow: "Herramienta interna de ejemplo",
      exampleTitle: "Un centro de operaciones único para una empresa de servicios multi-sucursal",
      exampleIntro1:
        "Una empresa de servicios con varias sucursales puede recibir solicitudes de clientes por teléfono, formularios web, correo, empleados y socios externos.",
      exampleIntro2:
        "Luego los empleados usan sistemas separados para registros de clientes, agenda, documentos, facturación, comunicación y coordinación de contratistas.",
      exampleIntro3:
        "En vez de obligar al equipo a manejar cada solicitud manualmente en todas las plataformas, Kranbos podría construir un centro de operaciones interno que reúna el trabajo relevante en una interfaz organizada.",
      exampleFeatures: [
        {
          title: "Bandeja de solicitudes",
          text: "Ve las solicitudes de clientes, empleados y sucursales en una sola cola.",
        },
        {
          title: "Detalle de la solicitud",
          text: "Consulta el cliente, la sucursal, la categoría, los documentos de respaldo, la actividad reciente y la información relevante de los sistemas conectados.",
        },
        {
          title: "Asignación",
          text: "Asigna la solicitud a un empleado, gerente, técnico o contratista autorizado.",
        },
        {
          title: "Seguimiento de estado",
          text: "Mueve el trabajo por etapas como Nueva, Esperando información, En revisión, Aprobada, Asignada, En curso y Completa.",
        },
        {
          title: "Controles de aprobación",
          text: "Exige la aprobación de un gerente antes de avanzar con presupuestos, gastos, cambios de acceso u otras acciones importantes.",
        },
        {
          title: "Historial de actividad",
          text: "Ve comentarios, archivos, asignaciones, cambios de estado y acciones completadas en orden cronológico.",
        },
        {
          title: "Alertas de excepciones",
          text: "Destaca trabajo vencido, información faltante, integraciones fallidas y solicitudes que quedaron inactivas.",
        },
        {
          title: "Vista de gerencia",
          text: "Permite que los gerentes vean la carga de trabajo, los cuellos de botella y los temas sin resolver entre sucursales.",
        },
      ],
      exampleWorkflowTitle: "Un recorrido típico dentro de la herramienta",
      exampleWorkflowSteps: [
        "Solicitud recibida",
        "Información revisada",
        "Datos faltantes solicitados",
        "Aprobación de gerencia",
        "Trabajo asignado",
        "Finalización confirmada",
        "Registro actualizado",
      ],
      exampleClosing:
        "La herramienta interna no tiene que reemplazar todas las plataformas existentes. Puede convertirse en la capa operativa que ayuda a los empleados a interactuar con esos sistemas de forma más consistente.",
      exampleDisclaimer:
        "Es un ejemplo ilustrativo, no un proyecto real de un cliente. Los nombres, registros y datos mostrados son ficticios.",

      systemsEyebrow: "Trabajamos con lo que ya tienes",
      systemsTitle: "Una mejor interfaz sin reemplazar toda tu tecnología",
      systemsBody1:
        "La mayoría de las herramientas internas necesitan convivir con el software del que el negocio ya depende.",
      systemsBody2:
        "Kranbos evalúa cómo la nueva herramienta puede intercambiar información de forma segura con las plataformas existentes, reducir la carga duplicada y dar acceso a la información necesaria para cada tarea.",
      systemsGroups: [
        {
          title: "Clientes y comunicación",
          items: ["CRM y plataformas de gestión de clientes", "Correo y calendarios", "Sistemas de agenda"],
        },
        {
          title: "Documentos y firmas",
          items: ["Almacenamiento de documentos", "Adobe Acrobat", "Plataformas de firma electrónica"],
        },
        {
          title: "Suites de trabajo",
          items: ["Microsoft 365", "Google Workspace", "Herramientas de proyectos y tareas"],
        },
        {
          title: "Operaciones y datos",
          items: [
            "Software de contabilidad y facturación",
            "Hojas de cálculo",
            "Plataformas específicas de la industria",
            "Bases de datos internas",
            "APIs de terceros autorizadas",
          ],
        },
      ],
      systemsSupporting:
        "Según la plataforma, la información puede sincronizarse automáticamente, consultarse cuando se necesita, enlazarse a su fuente original o revisarse mediante un paso manual controlado.",
      systemsAdditional:
        "Cuando un sistema no admite la integración requerida, Kranbos identifica la alternativa práctica más segura en lugar de prometer funcionalidad que la plataforma no puede sostener de forma confiable.",

      securityEyebrow: "Diseñado según la responsabilidad de cada persona",
      securityTitle: "Que cada persona vea solo lo que necesita para su trabajo",
      securityBody1:
        "Una herramienta interna puede ofrecer una experiencia más controlada que darle a cada empleado o contratista acceso completo a varias plataformas del negocio.",
      securityBody2:
        "Kranbos puede diseñar la interfaz alrededor de cuentas separadas, roles de usuario, acciones limitadas, requisitos de aprobación y un historial de actividad más claro.",
      securityUsefulLabel: "Esto es especialmente útil cuando el negocio trabaja con:",
      securityUsefulItems: [
        "Contratistas externos",
        "Asistentes virtuales",
        "Personal en el exterior",
        "Empleados temporales",
        "Varios departamentos",
        "Varias sucursales",
        "Socios externos",
      ],
      security1Title: "Acceso por rol",
      security1Text:
        "Controla qué secciones, registros, campos y acciones están disponibles para cada usuario.",
      security2Title: "Puntos de aprobación",
      security2Text:
        "Exige una revisión autorizada antes de completar acciones sensibles o irreversibles.",
      security3Title: "Exposición de datos controlada",
      security3Text:
        "Muestra solo la información necesaria para la tarea asignada en vez de dar acceso amplio al sistema.",
      security4Title: "Historial de actividad",
      security4Text:
        "Registra asignaciones, actualizaciones, aprobaciones, cargas de archivos y cambios de estado importantes.",
      securityDisclaimer:
        "Kranbos implementa controles operativos y de aplicación prácticos como parte de la solución. Las auditorías formales de ciberseguridad, las revisiones legales de cumplimiento y las certificaciones de seguridad pueden requerir un proveedor especializado.",

      comboEyebrow: "Interfaz + automatización",
      comboTitle: "La herramienta le da control a tu equipo. La automatización mantiene el trabajo en movimiento.",
      comboBody1: "Las herramientas internas y la automatización de flujos suelen trabajar juntas.",
      comboBody2:
        "La herramienta interna le da a los empleados un lugar claro para revisar información, tomar decisiones y completar el trabajo. La automatización se encarga de las acciones repetitivas en segundo plano: crear registros, enviar recordatorios, pedir documentos, actualizar sistemas y notificar a las personas correctas.",
      comboToolTitle: "Herramienta Interna",
      comboToolItems: [
        "Ver y gestionar el trabajo",
        "Revisar información",
        "Tomar decisiones",
        "Asignar responsabilidades",
        "Aprobar o rechazar solicitudes",
        "Resolver excepciones",
        "Actualizar el estado",
      ],
      comboAutomationTitle: "Automatización de Flujos",
      comboAutomationItems: [
        "Mover datos entre sistemas",
        "Crear registros y tareas",
        "Enviar notificaciones",
        "Pedir información faltante",
        "Generar documentos",
        "Escalar trabajo vencido",
        "Sincronizar actualizaciones aprobadas",
      ],
      comboClosing:
        "Kranbos puede construir cada capacidad por separado o combinarlas en una sola solución operativa.",
      comboCta: "Conocer Automatización de Flujos",

      whenEyebrow: "La solución correcta",
      whenTitle: "No todo problema necesita software a medida",
      whenBody1:
        "Kranbos empieza por entender el problema del negocio antes de recomendar una solución técnica.",
      whenBody2:
        "En algunos casos, una plataforma existente, un cambio de configuración o una automatización pueden ser suficientes. Una herramienta interna a medida tiene sentido cuando el equipo necesita una experiencia operativa específica que los productos existentes no pueden ofrecer de forma eficiente.",
      whenYesTitle: "Una herramienta interna puede ser apropiada cuando:",
      whenYesItems: [
        "Los empleados esquivan el software existente una y otra vez",
        "La misma información debe revisarse en varios sistemas",
        "El proceso requiere roles, pasos o aprobaciones a medida",
        "Los gerentes necesitan una cola operativa compartida",
        "Los contratistas necesitan acceso restringido",
        "Los procesos basados en hojas de cálculo se vuelven difíciles de controlar",
        "El software existente tiene demasiada complejidad o poca flexibilidad",
        "El flujo de trabajo es lo bastante importante como para justificar una interfaz dedicada",
      ],
      whenNoTitle: "Una herramienta a medida puede no ser necesaria cuando:",
      whenNoItems: [
        "Una plataforma existente ya resuelve bien el problema",
        "El proceso es temporal o se usa muy poco",
        "Una automatización simple puede eliminar el cuello de botella",
        "El flujo de trabajo todavía no está claramente definido",
        "El negocio no está preparado para mantener o adoptar un proceso nuevo",
      ],
      whenClosing:
        "Nuestro objetivo es recomendar la solución más pequeña y práctica que mejore de forma real la manera en que opera el negocio.",

      processEyebrow: "Cómo funciona",
      processTitle: "Construir alrededor de las personas que hacen el trabajo",
      processSteps: [
        {
          title: "Descubrimiento operativo",
          text: "Documentamos el proceso actual, los usuarios, las responsabilidades, los sistemas, los parches manuales y los problemas recurrentes.",
        },
        {
          title: "Definición de la herramienta",
          text: "Identificamos las pantallas, acciones, roles, integraciones y reglas de negocio esenciales para la primera versión útil.",
        },
        {
          title: "Prototipo y validación",
          text: "Creamos una dirección de interfaz práctica y confirmamos que refleja cómo trabaja el equipo antes de construir la solución completa.",
        },
        {
          title: "Desarrollo y pruebas",
          text: "Construimos la herramienta, conectamos los sistemas autorizados, configuramos permisos y probamos los flujos importantes y los casos límite.",
        },
        {
          title: "Lanzamiento y mejora",
          text: "Implementamos la herramienta, documentamos su uso, acompañamos la adopción y la mejoramos con el feedback operativo real.",
        },
      ],

      outcomesEyebrow: "El resultado",
      outcomesTitle: "Una forma más clara de manejar el trabajo detrás del negocio",
      outcome1Title: "Un espacio de trabajo organizado",
      outcome1Text:
        "Dale a los empleados un lugar consistente para encontrar, revisar y completar el trabajo operativo.",
      outcome2Title: "Menos saltos entre sistemas",
      outcome2Text:
        "Reúne la información y las acciones que un proceso necesita en una experiencia más enfocada.",
      outcome3Title: "Responsabilidades más claras",
      outcome3Text:
        "Hace más fácil entender quién es responsable, el estado, las aprobaciones y los próximos pasos.",
      outcome4Title: "Mejor control operativo",
      outcome4Text:
        "Reduce la dependencia de hojas de cálculo dispersas, bandejas de correo, mensajes informales y la memoria de cada empleado.",

      relatedTitle: "La mejor solución puede combinar más de un servicio",
      relatedIntro:
        "Kranbos puede combinar herramientas internas, automatización, reportes y mejoras de hojas de cálculo en un solo sistema operativo práctico.",
      relatedCurrentLabel: "Estás aquí",
      related1Text: "Dale a tu equipo una interfaz enfocada para gestionar el trabajo operativo.",
      related2Text:
        "Conecta los sistemas existentes y mueve información y acciones automáticamente.",
      related3Text:
        "Sigue la actividad, la carga de trabajo, los cuellos de botella, las excepciones y el desempeño del negocio.",
      related4Text:
        "Mejora o reemplaza procesos basados en hojas de cálculo que se volvieron difíciles de mantener.",

      finalTitle: "Dale a tu equipo una mejor forma de sacar el trabajo adelante",
      finalBody:
        "Cuéntanos dónde tus sistemas actuales, hojas de cálculo y procesos manuales están frenando al equipo. Te ayudamos a determinar si una herramienta interna, una automatización o una solución más simple es el próximo paso correcto.",
      finalSecondaryCta: "Contáctanos",
    },
  },
};
